import { useState } from 'react';
import { cx } from '../../ui';

// Forwarding is the real conversion event for outbound visitors. Mailto with a pre-written note, plus copy link.
export function pageUrl() {
  if (typeof window === 'undefined') return 'https://www.brandedmark.com/';
  const u = new URL(window.location.href);
  u.searchParams.delete('v');
  u.hash = '';
  if (u.hostname === 'localhost') return `https://www.brandedmark.com${u.pathname}`;
  return u.toString();
}

export function mailtoHref(subject: string, body: string) {
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

const MailIcon = () => (
  <svg width="17" height="17" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <rect x="2.5" y="4.5" width="15" height="11" rx="2.5" stroke="currentColor" strokeWidth="1.7" />
    <path d="m3.5 6 6.5 5 6.5-5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const LinkIcon = () => (
  <svg width="17" height="17" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M8.5 11.5a3.5 3.5 0 0 0 5 0l2.5-2.5a3.5 3.5 0 0 0-5-5l-1 1M11.5 8.5a3.5 3.5 0 0 0-5 0L4 11a3.5 3.5 0 0 0 5 5l1-1" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);
const Tick = () => (
  <svg width="17" height="17" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="m4.5 10.5 3.5 3.5 7.5-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export function useCopy() {
  const [copied, setCopied] = useState(false);
  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
      } catch {
        /* ignore */
      }
      ta.remove();
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };
  return [copied, copy] as const;
}

/** Two buttons: email a colleague (pre-written) and copy the link. dark = on indigo grounds. */
export function ShareButtons({ subject, body, dark = false, label = 'Send to a colleague', className }: { subject: string; body: (url: string) => string; dark?: boolean; label?: string; className?: string }) {
  const [copied, copy] = useCopy();
  const ghost = dark
    ? 'text-white shadow-[inset_0_0_0_1.5px_rgba(255,255,255,.28)] hover:shadow-[inset_0_0_0_1.5px_#fff]'
    : 'text-bm-indigo shadow-[inset_0_0_0_1.5px_#E4E3F0] hover:shadow-[inset_0_0_0_1.5px_#241E61]';
  return (
    <div className={cx('flex flex-wrap items-center gap-2.5', className)}>
      <a className={cx('btn btn-sm', ghost)} href={mailtoHref(subject, body(pageUrl()))}>
        <MailIcon />
        {label}
      </a>
      <button type="button" className={cx('btn btn-sm', ghost)} onClick={() => copy(pageUrl())} aria-live="polite">
        {copied ? <Tick /> : <LinkIcon />}
        {copied ? 'Link copied' : 'Copy link'}
      </button>
    </div>
  );
}
